import { useId, useState, type ReactNode } from 'react'
import { labelledIndices } from '@/components/charts/axis'
import { formatNumber } from '@/lib/format'

export interface LinePoint {
  readonly label: string
  readonly value: number
}

interface LineChartProps {
  readonly data: readonly LinePoint[]
  readonly seriesName: string
  readonly height?: number
}

/*
 * Links ist bewusst mehr Platz als rechts. Dort steht die
 * Achsenbeschriftung, und ein sechsstelliges Suchvolumen mit
 * Tausenderpunkt braucht mehr als die 48 Einheiten, die hier
 * ursprünglich standen. Zu wenig Platz schneidet die Zahl ab, ohne
 * dass irgendetwas fehlschlägt.
 */
const PADDING = { top: 12, right: 12, bottom: 24, left: 58 } as const
const VIEW_WIDTH = 640

/**
 * Liniendiagramm für Verläufe über die Zeit.
 *
 * Nur eine Serie, deshalb keine Legende: Der Titel der Karte benennt sie
 * bereits. Eine Legendenbox für eine einzelne Linie ist reine Deko.
 *
 * Bewusst kein Fremdpaket. Ein Verlauf ist ein Pfad plus ein paar Achsen,
 * und mit eigenem SVG behalten wir die Kontrolle über Strichstärken,
 * Abstände und Farben. Wer hier eine Diagramm-Bibliothek einführt, holt
 * sich deren Voreinstellungen mit ins Haus.
 */
export function LineChart({ data, seriesName, height = 220 }: LineChartProps): ReactNode {
  const gradientId = useId()
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)

  if (data.length < 2) {
    return (
      <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
        Zu wenige Datenpunkte für einen Verlauf.
      </p>
    )
  }

  const values = data.map((point) => point.value)
  const maxValue = Math.max(...values)
  const minValue = Math.min(...values, 0)
  const range = maxValue - minValue || 1

  const plotWidth = VIEW_WIDTH - PADDING.left - PADDING.right
  const plotHeight = height - PADDING.top - PADDING.bottom

  const xFor = (index: number): number => PADDING.left + (index / (data.length - 1)) * plotWidth
  const yFor = (value: number): number =>
    PADDING.top + plotHeight - ((value - minValue) / range) * plotHeight

  const linePath = data
    .map(
      (point, index) =>
        `${index === 0 ? 'M' : 'L'} ${String(xFor(index))} ${String(yFor(point.value))}`,
    )
    .join(' ')

  const areaPath =
    `${linePath} L ${String(xFor(data.length - 1))} ${String(PADDING.top + plotHeight)}` +
    ` L ${String(xFor(0))} ${String(PADDING.top + plotHeight)} Z`

  // Drei Gitterlinien reichen zum Einordnen. Mehr wird zum Raster,
  // durch das man die Daten kaum noch sieht.
  const gridValues = [minValue, minValue + range / 2, maxValue]

  const active = hoverIndex === null ? null : data[hoverIndex]

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${String(VIEW_WIDTH)} ${String(height)}`}
        className="w-full"
        role="img"
        aria-label={`Verlauf: ${seriesName}`}
        onMouseLeave={() => {
          setHoverIndex(null)
        }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--viz-series-1)" stopOpacity="0.18" />
            <stop offset="100%" stopColor="var(--viz-series-1)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {gridValues.map((value) => (
          <g key={value}>
            <line
              x1={PADDING.left}
              y1={yFor(value)}
              x2={VIEW_WIDTH - PADDING.right}
              y2={yFor(value)}
              stroke="var(--viz-grid)"
              strokeWidth="1"
            />
            <text
              x={PADDING.left - 8}
              y={yFor(value) + 4}
              textAnchor="end"
              className="fill-slate-500 text-[11px] tabular-nums dark:fill-slate-400"
            >
              {formatNumber(Math.round(value))}
            </text>
          </g>
        ))}

        <path d={areaPath} fill={`url(#${gradientId})`} />
        <path
          d={linePath}
          fill="none"
          stroke="var(--viz-series-1)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {data.map((point, index) => (
          <g key={point.label}>
            {/* Trefferfläche deutlich größer als der Punkt selbst. */}
            <rect
              x={xFor(index) - plotWidth / (data.length - 1) / 2}
              y={PADDING.top}
              width={plotWidth / (data.length - 1)}
              height={plotHeight}
              fill="transparent"
              onMouseEnter={() => {
                setHoverIndex(index)
              }}
            />
            {hoverIndex === index && (
              <circle
                cx={xFor(index)}
                cy={yFor(point.value)}
                r="4.5"
                fill="var(--viz-series-1)"
                stroke="var(--color-white)"
                strokeWidth="2"
              />
            )}
          </g>
        ))}

        {labelledIndices(data.length).map((index) => {
          const point = data[index]
          if (!point) return null
          /*
           * Am Rand wird der Text nach innen ausgerichtet. Mittig
           * zentriert würde die Hälfte der ersten und der letzten
           * Beschriftung außerhalb des SVG liegen und still wegfallen.
           */
          const anchor =
            index === 0 ? 'start' : index === data.length - 1 ? 'end' : ('middle' as const)
          return (
            <text
              key={point.label}
              x={xFor(index)}
              y={height - 6}
              textAnchor={anchor}
              className="fill-slate-500 text-[11px] dark:fill-slate-400"
            >
              {point.label}
            </text>
          )
        })}
      </svg>

      {active && (
        <div className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 rounded border border-slate-200 bg-white px-2.5 py-1.5 text-xs shadow-sm dark:border-slate-600 dark:bg-slate-800">
          <span className="text-slate-500 dark:text-slate-400">{active.label}: </span>
          <span className="font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {formatNumber(active.value)}
          </span>
        </div>
      )}
    </div>
  )
}
